import React, { useState, useEffect } from 'react'
import useCreateUser from 'sdk/hooks/user/useCreateUser';
import Button from 'components/shared/Button';
import { PhoneInput } from 'components/shared/Input';
import TermsOfUseLinks from 'components/shared/TermsOfUseLinks';
import ConfirmView from 'modules/auth/components/ConfirmView';
import useCheckIfUserExists from 'sdk/hooks/user/useCheckIfUserExists';
import { useSessionStorage } from 'usehooks-ts'
import MessageModal from 'components/shared/modals/MessageModal';
import { StringConstant } from 'utils/Constants';
import SignInLink from './SignInLink';
import styles from './SignUp.module.scss';

type PhoneViewProps = {
  handleNext: () => void,
  goToSignIn: () => void,
  className?: string,
}

const defaultErrMessage = 'Check the correctness of your sign up data'

function PhoneView({
  handleNext,
  goToSignIn,
  className,
}: PhoneViewProps) {
  const [isPhoneValid, setPhoneValid] = useState(false)
  const [isVerification, setIsVerification] = useState(false)
  const [phone, setPhone] = useState('')
  const [showPhoneError, setShowPhoneError] = useState<boolean>(false)
  const [phoneError, setPhoneError] = useState('')
  const [, setDefaultPhone] = useSessionStorage<any>('defaultPhone', null)

  const {
    mutate: signUpMutation,
    error: signUpError,
    isSuccess: isSignUpSuccess,
    isError: isSignUpError,
    isLoading: isSignupLoading,
  } = useCreateUser({ phone })

  const handlePhoneChange = (value: string, isValid: boolean) => {
    setPhone(value)
    setPhoneValid(isValid)
    setShowPhoneError(false)
    setPhoneError('')
  }

  const {
    mutate: getAccounts,
    data: getAccountsData,
    isSuccess: isGetAccountsSuccess,
    isError: isGetAccountsError,
    error: getAccountsError,
    reset: resetGetAccounts,
    isLoading: isGetAccountsLoading,
  } = useCheckIfUserExists({ phone })

  useEffect(() => {
    if (signUpError) {
      setPhoneError(StringConstant.SIGN_UP_ERROR_MEESAGE)
      setShowPhoneError(true)
    }
  }, [isSignUpError])

  useEffect(() => {
    if (isGetAccountsError) {
      setPhoneError(StringConstant.SIGN_UP_ERROR_MEESAGE)
      setShowPhoneError(true)
      resetGetAccounts();
    }
  }, [isGetAccountsError])

  useEffect(() => {
    if (isGetAccountsSuccess) {
      if (!getAccountsData) {
        signUpMutation()
      } else if (getAccountsData !== 'deleted') {
        // setError('Seems like you have an account')
        setDefaultPhone(phone)
        goToSignIn()
      }
    }
  }, [isGetAccountsSuccess, getAccountsData])

  useEffect(() => {
    if (isSignUpSuccess) setIsVerification(true)
  }, [isSignUpSuccess])
  // @ts-ignore
  if (getAccountsData === 'deleted') {
    return (
      <MessageModal
        title="Error"
        status="sorry"
        // @ts-ignore
        onClick={() => resetGetAccounts()}
        message="Sorry! This account has been deleted. Please reach out to us to initiate the account reactivation process"
      />
    )
  }

  if (isVerification) {
    return (
      <div className={className}>
        <div className={styles.subheader}>
          Please enter the code we texted you
        </div>
        <ConfirmView
          handleNext={handleNext}
          resendToken={() => signUpMutation()}
          phone={phone}
          white
        />
      </div>
    )
  }

  return (
    <div className={className}>
      <PhoneInput
        className={styles.phoneInput}
        value={phone}
        placeholder="Phone Number"
        onChange={handlePhoneChange}
        error={showPhoneError || isGetAccountsError}
        errorMessage={phoneError || defaultErrMessage}
        onBlur={() => setShowPhoneError(!isPhoneValid)}
        white
        wide
      />

      <Button
        className={styles.margin20}
        disabled={!isPhoneValid || isSignupLoading || isGetAccountsLoading}
        onClick={() => getAccounts()}
        loading={isSignupLoading}
        purple
        wide
      >
        Send Code
      </Button>

      <div className={styles.panelFooter}>
        <SignInLink onClick={goToSignIn} />
        <TermsOfUseLinks />
      </div>
    </div>
  )
}

export default PhoneView
